import dataApiFactory from '~/api/dataApiFactory'
import type { RestDataApi } from '@yakit/core/api/dataApi'
import { isPayment } from '~/ar/tran/uitls/tranType'

/**
 * Entity types enum for journal entries
 */
export enum JournalEntityType {
  AR_TRAN_JOURNAL = 'ArTranJournal',
  AR_ADJUST_JOURNAL = 'ArAdjustJournal',
  AR_ADJUST_LINE_JOURNAL = 'ArAdjustLineJournal',
  AR_TRAN_CREATED = 'ArTran Created'
}

/**
 * Common includes for journal API calls
 */
const JOURNAL_INCLUDES = 'glPostBatch.id, glAcct, glAccount.name, debit, credit, arTran.refnum, id'

/**
 * Journal entry interface for combined data
 */
interface JournalEntry {
  id: number
  glAcct: string
  glAccount?: {
    name?: string
  }
  debit: number
  credit: number
  glPostBatch?: {
    id: number
  }
  arTran?: {
    id: number
    refnum: string
  }
  arAdjust?: {
    arTran: {
      id: number
      refnum: string
    }
  }
  arAdjustLine?: {
    arTran: {
      id: number
      refnum: string
    }
  }
  entity: JournalEntityType
  newArTran?: {
    id: number
  }
}

/**
 * Query parameters interface
 */
interface QueryParams {
  q?: {
    arTranId?: number
    'arAdjust.arTranId'?: number
    'arAdjustLine.arTranId'?: number
    'arTran.autoCash.createdByPayId'?: number
  }
  arTranId?: number
  docType?: string
  includes?: string
}

/**
 * API response interface
 */
interface ApiResponse {
  data: JournalEntry[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

/**
 * Combined Journal API that fetches and combines data from multiple journal sources
 * for a specific AR Transaction ID
 */
export default function(opts: any): RestDataApi {
  const tranJournalApi = dataApiFactory.gl.tranJournal
  const adjustJournalApi = dataApiFactory.gl.adjustJournal

  const combinedApi: RestDataApi = {
    ...tranJournalApi,
    key: 'ar/combinedJournal',
    path: 'ar/combinedJournal',
    configPath: 'ar/tranJournal', // Use existing config to avoid backend calls

    async search(params: QueryParams = {}): Promise<ApiResponse> {
      const results: JournalEntry[] = []
      const arTranId = params.q?.arTranId || params.arTranId
      
      if (!arTranId) {
        throw new Error('arTranId is required for combined journal query')
      }

      try {
       // 1. Fetch tranJournal data
       if(!isPayment(params.q)) {
        const tranJournalParams = { 
          q: { arTranId },
          includes: JOURNAL_INCLUDES
        }
        const tranJournalResponse = await tranJournalApi.search(tranJournalParams)
        const tranJournalData = tranJournalResponse.data || []
        tranJournalData.forEach((item: any) => {
          results.push({
            ...item,
            entity: JournalEntityType.AR_TRAN_JOURNAL
          })
        })
       }

        // 2. Fetch adjustJournal data for arAdjust
        const adjustJournalParams = { 
          q: { 'arAdjust.arTranId': arTranId },
          includes: JOURNAL_INCLUDES
        }
        const adjustJournalResponse = await adjustJournalApi.search(adjustJournalParams)
        const adjustJournalData = adjustJournalResponse.data || []
        adjustJournalData.forEach((item: any) => {
          results.push({
            ...item,
            entity: JournalEntityType.AR_ADJUST_JOURNAL
          })
        })

        // 3. Fetch adjustJournal data for arAdjustLine
        const adjustLineJournalParams = { 
          q: { 'arAdjustLine.arTranId': arTranId },
          includes: JOURNAL_INCLUDES
        }
        const adjustLineJournalResponse = await adjustJournalApi.search(adjustLineJournalParams)
        const adjustLineJournalData = adjustLineJournalResponse.data || []
        adjustLineJournalData.forEach((item: any) => {
          results.push({
            ...item,
            entity: JournalEntityType.AR_ADJUST_LINE_JOURNAL
          })
        })

        if(isPayment(params.q)) {
        // 4. Fetch tranJournal data for created arTran (only for payment transactions)
        const createdArTranParams = { 
          q: { 'arTran.autoCash.createdByPayId': arTranId },
          includes: JOURNAL_INCLUDES 
        }
        const createdArTranResponse = await tranJournalApi.search(createdArTranParams)
        const createdArTranData = createdArTranResponse.data || []
        createdArTranData.forEach((item: any) => {
          results.push({
            ...item,
            entity: JournalEntityType.AR_TRAN_CREATED,
            newArTran: {
              id: item.arTran?.id
            }
          })
        })
      }

        return {
          data: results,
          total: results.length,
          page: 1,
          pageSize: results.length,
          totalPages: 1
        }
      } catch (error) {
        console.error('Error fetching combined journal data:', error)
        return { data: [], total: 0, page: 1, pageSize: 0, totalPages: 0 }
      }
    },

    async query(params: QueryParams = {}): Promise<ApiResponse> {
      return this.search(params)
    },

    async picklist(params: QueryParams = {}): Promise<ApiResponse> {
      return this.search(params)
    }
  }

  return combinedApi
} 